import React, { useMemo, useState } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import {
  AppBar,
  Alert,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  TextField,
  Toolbar,
  Typography,
} from "@mui/material";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import { Wrapper } from "../../../components";
import tokens from "../../../shared/theme/tokens";
import {
  useGetAvailabilityQuery,
  useGetPropertyByCodeQuery,
} from "../../../store/services/publicApi";
import {
  useGetApplicationSelectionQuoteMutation,
  useUpdateApplicationSelectionMutation,
} from "../../../store/services/privateApi";
import {
  RegisterReviewSection,
  RegisterReviewSectionTitle,
} from "../../Account/Register/Register.style";
import { ChargesTable, EmptyState } from "../Availability.style";
import { FloorplanPlaceholder } from "./FloorplanPlaceholder";
import { AvailabilityResponse, FloorplanResult, UnitResult } from "../types";
import { currency, formatBedsBaths, shortDate } from "../format";
import { buildUnitApplyPath } from "../applyNavigation";
import {
  buildAvailabilityListPath,
  isApplicationReuseMode,
  paymentReturnPath,
  safeInternalPath,
} from "../availabilityReuse";
import { parseMoveInTooSoonError } from "../../../helpers/moveInTooSoon";
import { rentsDiffer } from "../../../helpers/rentConfirm";

const defaultMoveIn = () => {
  const d = new Date();
  d.setDate(d.getDate() + 14);
  return d.toISOString().slice(0, 10);
};

export const UnitDetailScreen: React.FC = () => {
  const { propertyCode, unitNumber } = useParams<{
    propertyCode: string;
    unitNumber: string;
  }>();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const moveInDate = searchParams.get("moveInDate") || defaultMoveIn();
  const requiresLeaseTermChoice =
    searchParams.get("chooseLeaseTerm") === "true";
  const reuseApplicationId = searchParams.get("applicationId");
  const isReuse = isApplicationReuseMode(searchParams);

  const { data, isLoading, error } = useGetAvailabilityQuery(
    {
      propertyCode: propertyCode || "",
      moveInDate,
      perPage: 200,
    },
    { skip: !propertyCode }
  ) as {
    data: AvailabilityResponse | undefined;
    isLoading: boolean;
    error: any;
  };
  const { data: propertyDetails } = useGetPropertyByCodeQuery(
    propertyCode || "",
    { skip: !propertyCode }
  ) as {
    data:
      | {
          id: string;
          affordableHousing?: boolean | null;
        }
      | undefined;
  };

  const unit: UnitResult | null = useMemo(() => {
    if (!data || !unitNumber) return null;
    return data.units.find((u) => u.unitNumber === unitNumber) ?? null;
  }, [data, unitNumber]);

  const floorplan: FloorplanResult | null = useMemo(() => {
    if (!data || !unit) return null;
    return data.floorplans.find((f) => f.unitType === unit.unitType) ?? null;
  }, [data, unit]);

  const [selectedTerm, setSelectedTerm] = useState<number | null>(null);

  const effectiveTerm = useMemo(() => {
    if (!unit) return null;
    if (selectedTerm !== null) {
      return unit.matchedTerms.find((t) => t.term === selectedTerm) ?? null;
    }
    if (requiresLeaseTermChoice) return null;
    // Prefer 12 months when available — shortest term (often 6) is a poor default
    // and may not match what leasing offers in ResMan.
    return (
      unit.matchedTerms.find((t) => t.term === 12) ??
      unit.matchedTerms.find((t) => t.term >= 12) ??
      unit.matchedTerms[unit.matchedTerms.length - 1] ??
      null
    );
  }, [unit, selectedTerm, requiresLeaseTermChoice]);

  const canApply = Boolean(unit && effectiveTerm && propertyDetails);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [saveError, setSaveError] = useState("");
  const [previousRent, setPreviousRent] = useState<number | null>(null);
  const [quotedRent, setQuotedRent] = useState<number | null>(null);
  const [quoteLoading, setQuoteLoading] = useState(false);
  const [getSelectionQuote] = useGetApplicationSelectionQuoteMutation();
  const [updateSelection, { isLoading: isSaving }] =
    useUpdateApplicationSelectionMutation();

  const persistSelection = async () => {
    if (!unit || !effectiveTerm || !reuseApplicationId) return;
    setSaveError("");
    const res: any = await updateSelection({
      id: reuseApplicationId,
      unitNumber: unit.unitNumber,
      moveInDate,
      term: String(effectiveTerm.term),
    });
    if (res?.error) {
      const tooSoon = parseMoveInTooSoonError(res.error);
      setSaveError(
        tooSoon?.message ??
          res.error?.data?.message ??
          "We couldn't update this application. Please try another unit."
      );
      setConfirmOpen(false);
      return;
    }
    setConfirmOpen(false);
    const fallback = paymentReturnPath(reuseApplicationId);
    navigate(safeInternalPath(searchParams.get("returnTo"), fallback));
  };

  const onApply = () => {
    if (!unit || !effectiveTerm) return;
    if (isReuse && reuseApplicationId) {
      setSaveError("");
      setQuotedRent(null);
      setPreviousRent(null);
      setQuoteLoading(true);
      setConfirmOpen(true);
      getSelectionQuote({
        id: reuseApplicationId,
        unitNumber: unit.unitNumber,
        moveInDate,
        term: String(effectiveTerm.term),
      })
        .then((res: any) => {
          const tooSoon = parseMoveInTooSoonError(res?.error);
          if (tooSoon) {
            setConfirmOpen(false);
            setSaveError(tooSoon.message);
            return;
          }
          if (res?.error) {
            setQuotedRent(effectiveTerm.effectiveRent);
            return;
          }
          const rent = Number(res?.data?.rent);
          const prev = Number(res?.data?.previousRent);
          setQuotedRent(
            Number.isFinite(rent) ? rent : effectiveTerm.effectiveRent
          );
          if (Number.isFinite(prev)) setPreviousRent(prev);
        })
        .finally(() => setQuoteLoading(false));
      return;
    }
    navigate(
      buildUnitApplyPath({
        property: propertyDetails,
        fallbackPropertyId: data?.property.id,
        unitID: unit.unitNumber,
        moveInDate,
        leaseTerm: String(effectiveTerm.term),
      })
    );
  };

  const onMoveInChange = (newDate: string) => {
    const params = new URLSearchParams(searchParams);
    params.set("moveInDate", newDate);
    setSearchParams(params, { replace: true });
    setSelectedTerm(null);
  };

  const onBack = () => {
    navigate(
      buildAvailabilityListPath({
        propertyCode: propertyCode || "",
        moveInDate,
        searchParams,
      })
    );
  };

  const photo =
    floorplan?.images.find((i) => i.type === "Floorplan") ??
    floorplan?.images[0] ??
    null;

  const charges = effectiveTerm?.recurringCharges ?? [];
  const chargesTotal = charges.reduce((sum, c) => sum + c.amount, 0);
  const totalRent = (effectiveTerm?.effectiveRent ?? 0) + chargesTotal;
  const displayNewRent = quotedRent ?? effectiveTerm?.effectiveRent ?? null;
  const rentChanged = rentsDiffer(previousRent, displayNewRent);

  const today = new Date().toISOString().slice(0, 10);
  const minMoveIn =
    unit && unit.madeReadyDate && unit.madeReadyDate > today
      ? unit.madeReadyDate
      : today;

  const amenities = useMemo(() => {
    if (!data || !unit) return [];
    const combined = [
      ...(data.propertyAmenities ?? []),
      ...(unit.amenities ?? []),
    ];
    const seen = new Set<string>();
    const deduped: string[] = [];
    for (const a of combined) {
      const key = a.description.trim().toLowerCase();
      if (!key || seen.has(key)) continue;
      seen.add(key);
      deduped.push(a.description.trim());
    }
    return deduped.sort((a, b) => a.localeCompare(b));
  }, [data, unit]);

  return (
    <Wrapper type="xl">
      <AppBar position="static">
        <Toolbar sx={{ maxWidth: 1400, mx: "auto", width: "100%", px: 2 }}>
          <Button
            onClick={onBack}
            startIcon={<ArrowBackIcon />}
            sx={{ color: "white", mr: 2 }}
          >
            Back
          </Button>
          <Typography variant="h4" noWrap>
            {data?.property.name || "Unit"}
          </Typography>
        </Toolbar>
      </AppBar>

      {(isLoading || !data) && !error && (
        <Box sx={{ display: "flex", justifyContent: "center", p: 6 }}>
          <CircularProgress />
        </Box>
      )}

      {error && (
        <EmptyState sx={{ m: 3 }}>
          Something went wrong loading unit details.
        </EmptyState>
      )}

      {data && !unit && (
        <EmptyState sx={{ m: 3 }}>
          Unit {unitNumber} is not currently available. It may be leased or the
          move-in date is outside its pricing window.{" "}
          <Button size="small" onClick={onBack} sx={{ ml: 1 }}>
            Back to availability
          </Button>
        </EmptyState>
      )}

      {data && unit && floorplan && (
        <Box
          sx={{
            maxWidth: 1400,
            mx: "auto",
            p: 3,
            display: "grid",
            gridTemplateColumns: { xs: "1fr", md: "minmax(0, 1.5fr) 360px" },
            gap: 3,
          }}
        >
          {/* Left: hero floor plan + summary */}
          <Box>
            <Box
              sx={{
                aspectRatio: "4 / 3",
                background: tokens.colors.pageBg,
                borderRadius: 2,
                overflow: "hidden",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                mb: 2,
              }}
            >
              {photo ? (
                <img
                  src={photo.src}
                  alt={photo.name ?? floorplan.name}
                  style={{
                    width: "100%",
                    height: "100%",
                    objectFit: "contain",
                  }}
                />
              ) : (
                <FloorplanPlaceholder />
              )}
            </Box>

            <RegisterReviewSection>
              <Box
                sx={{ display: "flex", justifyContent: "space-between", mb: 1 }}
              >
                <Box>
                  <Typography variant="h3Bold" sx={{ fontSize: 22 }}>
                    {formatBedsBaths(unit.bedrooms, unit.bathrooms)}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Unit {unit.unitNumber} · {unit.unitType} · {floorplan.name}
                  </Typography>
                </Box>
                <Typography variant="h3Bold" sx={{ fontSize: 22 }}>
                  {effectiveTerm
                    ? `${currency(effectiveTerm.effectiveRent, 2)}/mo`
                    : currency(unit.minEffectiveRent, 2) + "/mo"}
                </Typography>
              </Box>

              <RegisterReviewSectionTitle>Summary</RegisterReviewSectionTitle>
              <Box
                sx={{
                  display: "grid",
                  gridTemplateColumns: "1fr 1fr 1fr",
                  gap: 1,
                  fontSize: 13,
                  mb: 2,
                }}
              >
                <Box>
                  <strong>Sq Ft:</strong>{" "}
                  {unit.minSqft === unit.maxSqft
                    ? unit.minSqft
                    : `${unit.minSqft}–${unit.maxSqft}`}
                </Box>
                <Box>
                  <strong>Building:</strong> {unit.buildingName || "—"}
                </Box>
                <Box>
                  <strong>Floor:</strong> {unit.floorLevel ?? "—"}
                </Box>
                <Box>
                  <strong>Available:</strong> {shortDate(unit.madeReadyDate)}
                </Box>
                <Box>
                  <strong>Deposit:</strong>{" "}
                  {unit.depositAmount > 0 ? currency(unit.depositAmount) : "—"}
                </Box>
              </Box>

              {unit.activeConcession && (
                <Alert severity="success" sx={{ mb: 2 }}>
                  <strong>{unit.activeConcession.header || "Special"}:</strong>{" "}
                  {unit.activeConcession.body ||
                    `$${unit.activeConcession.value} off`}
                </Alert>
              )}

              {charges.length > 0 && (
                <>
                  <RegisterReviewSectionTitle>
                    Other Charges
                  </RegisterReviewSectionTitle>
                  <ChargesTable>
                    <thead>
                      <tr>
                        <th>Description</th>
                        <th>Amount</th>
                      </tr>
                    </thead>
                    <tbody>
                      {charges.map((c, i) => (
                        <tr key={i}>
                          <td>{c.description}</td>
                          <td>{currency(c.amount, 2)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </ChargesTable>
                </>
              )}

              {effectiveTerm && charges.length > 0 && (
                <Box
                  sx={{
                    mt: 2,
                    pt: 2,
                    borderTop: `1px solid ${tokens.colors.border}`,
                  }}
                >
                  <Typography variant="body2">
                    <strong>Total monthly</strong> (rent + other charges):{" "}
                    <strong>{currency(totalRent, 2)}</strong>
                  </Typography>
                </Box>
              )}

              {amenities.length > 0 && (
                <Box sx={{ mt: 3 }}>
                  <RegisterReviewSectionTitle>
                    Apartment Features
                  </RegisterReviewSectionTitle>
                  <Box
                    component="ul"
                    sx={{
                      listStyle: "none",
                      p: 0,
                      m: 0,
                      display: "grid",
                      gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" },
                      columnGap: 3,
                      rowGap: 1,
                    }}
                  >
                    {amenities.map((a) => (
                      <Box
                        key={a}
                        component="li"
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          gap: 1,
                          fontSize: 14,
                          color: "text.secondary",
                        }}
                      >
                        <Box
                          component="span"
                          aria-hidden
                          sx={{
                            color: "text.disabled",
                            fontSize: 18,
                            lineHeight: 1,
                            fontWeight: 300,
                          }}
                        >
                          +
                        </Box>
                        {a}
                      </Box>
                    ))}
                  </Box>
                </Box>
              )}
            </RegisterReviewSection>
          </Box>

          {/* Right: application info panel */}
          <Box>
            <RegisterReviewSection sx={{ position: "sticky", top: 16, mb: 0 }}>
              <RegisterReviewSectionTitle>
                Application Information
              </RegisterReviewSectionTitle>

              <TextField
                label="Move-in Date"
                type="date"
                value={moveInDate}
                onChange={(e) => onMoveInChange(e.target.value)}
                InputLabelProps={{ shrink: true }}
                inputProps={{ min: minMoveIn }}
                helperText={
                  unit.madeReadyDate && unit.madeReadyDate > today
                    ? `Unit available from ${shortDate(unit.madeReadyDate)}`
                    : undefined
                }
                fullWidth
                sx={{ mb: 2 }}
              />

              <FormControl
                fullWidth
                sx={{ mb: 2 }}
                disabled={!unit.matchedTerms.length}
              >
                <InputLabel
                  id="term-label"
                  shrink={requiresLeaseTermChoice ? true : undefined}
                >
                  Lease Term
                </InputLabel>
                <Select
                  labelId="term-label"
                  label="Lease Term"
                  value={effectiveTerm ? String(effectiveTerm.term) : ""}
                  displayEmpty={requiresLeaseTermChoice}
                  renderValue={(value) => {
                    if (!value) {
                      return (
                        <Box component="span" sx={{ color: "text.secondary" }}>
                          Select lease term
                        </Box>
                      );
                    }
                    const termOption = unit.matchedTerms.find(
                      (t) => String(t.term) === String(value)
                    );
                    return termOption
                      ? `${termOption.term} Months — ${currency(
                          termOption.effectiveRent,
                          2
                        )}/mo`
                      : "";
                  }}
                  onChange={(e) =>
                    setSelectedTerm(
                      e.target.value ? Number(e.target.value) : null
                    )
                  }
                >
                  {requiresLeaseTermChoice && (
                    <MenuItem value="" disabled>
                      Select lease term
                    </MenuItem>
                  )}
                  {unit.matchedTerms.map((t) => (
                    <MenuItem key={t.term} value={String(t.term)}>
                      {t.term} Months — {currency(t.effectiveRent, 2)}/mo
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>

              {requiresLeaseTermChoice && !effectiveTerm && (
                <Alert severity="info" sx={{ mb: 2 }}>
                  Select a lease term to continue.
                </Alert>
              )}

              {unit.matchedTerms.length === 0 && (
                <Alert severity="warning" sx={{ mb: 2 }}>
                  This unit is not available for the selected move-in date.
                  Please try a different date.
                </Alert>
              )}

              {saveError !== "" && (
                <Alert
                  severity="error"
                  sx={{ mb: 2 }}
                  onClose={() => setSaveError("")}
                >
                  {saveError}
                </Alert>
              )}

              <Button
                variant="contained"
                color="success"
                fullWidth
                disabled={!canApply || isSaving}
                onClick={onApply}
              >
                {isReuse ? "Use this unit" : "Apply Now"}
              </Button>
            </RegisterReviewSection>
          </Box>
        </Box>
      )}

      <Dialog
        open={confirmOpen}
        onClose={() => !isSaving && setConfirmOpen(false)}
      >
        <DialogTitle>Confirm unit and rent</DialogTitle>
        <DialogContent>
          <Typography variant="body2" sx={{ mb: 1 }}>
            This updates your current application. You will not fill out a new
            application.
          </Typography>
          <Typography variant="body2">
            <strong>Unit:</strong> {unit?.unitNumber}
          </Typography>
          <Typography variant="body2">
            <strong>Move-in:</strong> {shortDate(moveInDate)}
          </Typography>
          <Typography variant="body2">
            <strong>Lease term:</strong>{" "}
            {effectiveTerm ? `${effectiveTerm.term} months` : "—"}
          </Typography>
          {previousRent != null && (
            <Typography variant="body2" sx={{ mt: 1 }}>
              <strong>Previous rent:</strong> {currency(previousRent, 2)}/mo
            </Typography>
          )}
          <Typography variant="body1" sx={{ mt: 1 }}>
            <strong>New monthly rent:</strong>{" "}
            {quoteLoading
              ? "Looking up the current rent…"
              : displayNewRent != null
              ? `${currency(displayNewRent, 2)}/mo`
              : "—"}
          </Typography>
          {rentChanged && !quoteLoading && (
            <Alert severity="info" sx={{ mt: 2 }}>
              Monthly rent is changing. Confirm the new amount to continue.
            </Alert>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmOpen(false)} disabled={isSaving}>
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={persistSelection}
            disabled={isSaving || quoteLoading}
          >
            {isSaving
              ? "Saving…"
              : rentChanged
              ? "Confirm unit and rent"
              : "Confirm and continue"}
          </Button>
        </DialogActions>
      </Dialog>
    </Wrapper>
  );
};
